/**
 * Project List Page
 * @implements US-099 - Backoffice Dashboard View
 *
 * Minimal project listing page that shows all projects as links
 * to their respective dashboard pages.
 */

import { gql, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import type { ProjectSummary } from '@/types';

const MY_PROJECTS_QUERY = gql`
  query MyProjects {
    myProjects {
      id
      title
      description
      lifecycleState
    }
  }
`;

interface MyProjectsData {
  myProjects: ProjectSummary[];
}

/**
 * Displays a list of projects with links to their dashboards.
 * Route: / (home page)
 * @implements US-099 - Backoffice Dashboard View
 */
export const ProjectListPage = () => {
  const { data, loading, error } = useQuery<MyProjectsData>(MY_PROJECTS_QUERY);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Projects
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Select a project to view its dashboard.
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load projects: {error.message}
        </Alert>
      )}

      {data?.myProjects.length === 0 && (
        <Alert severity="info">
          No projects found. Create a project in the Customer Portal first.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {data?.myProjects.map((project) => (
          <Card key={project.id}>
            <CardActionArea
              component={Link}
              to={`/projects/${project.id}/dashboard`}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography variant="h6">{project.title}</Typography>
                    {project.description && (
                      <Typography variant="body2" color="text.secondary">
                        {project.description}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={project.lifecycleState}
                    size="small"
                    color={
                      project.lifecycleState === 'ACTIVE' ? 'success' : 'default'
                    }
                    variant="outlined"
                  />
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Container>
  );
};
